/**
 * uc-core's site pack speaks the step registry's own shape (camelCase, one English `label`,
 * percents and kilos as decimal strings). The phone's schema wants snake_case, Hindi and English
 * names, numbers and grams. These helpers turn a server row into the phone's shape; a row already
 * in the phone's shape (dev seed, older cache) passes through untouched.
 */

type Row = Record<string, unknown>

const isRow = (v: unknown): v is Row => !!v && typeof v === 'object' && !Array.isArray(v)

/** Hindi names for the registry's step, output and photo-point keys; unknown keys keep English. */
const STEP_HI: Record<string, string> = {
  CLEAN: 'सफ़ाई',
  DRY: 'सुखाना',
  DEHULL: 'छिलका उतारना',
  SPLIT: 'दाल बनाना',
  POLISH: 'पॉलिश',
  GRADE: 'ग्रेडिंग',
  SORT: 'छँटाई',
  PACK: 'पैकिंग',
  DELABEL: 'पाउच खाली करना',
}
const OUTPUT_HI: Record<string, string> = {
  PRODUCT: 'तैयार माल',
  WASTE: 'कचरा और झाड़न',
  KERNEL: 'दाना',
  SHELL: 'छिलका',
  BYPRODUCT: 'साइड माल',
  DUST: 'धूल',
  GRADE_A: 'ग्रेड ए',
  GRADE_B: 'ग्रेड बी',
  REJECTS: 'छँटा हुआ माल',
  LEFTOVER: 'बचा माल',
  SPILLAGE: 'गिरा माल',
  FILM: 'पाउच की पन्नी',
}
const PHOTO: Record<string, { hi: string; en: string }> = {
  HOPPER: { hi: 'हॉपर की फ़ोटो', en: 'Hopper photo' },
  DRUM: { hi: 'ड्रम की फ़ोटो', en: 'Drum photo' },
  OUTLET: { hi: 'आउटलेट की फ़ोटो', en: 'Outlet photo' },
  BED: { hi: 'सुखाने की जगह की फ़ोटो', en: 'Drying bed photo' },
}
/** Steps that need a moisture reading once per lot at the issue. */
const MOISTURE_STEPS = new Set(['DRY'])

const num = (v: unknown): unknown => {
  if (typeof v !== 'string' || v.trim() === '') return v
  const n = Number(v)
  return Number.isFinite(n) ? n : v
}
const kgToG = (v: unknown): number | null | undefined => {
  if (v == null) return v as null | undefined
  const n = Number(v)
  return Number.isFinite(n) ? Math.round(n * 1000) : null
}

function output(o: unknown): unknown {
  if (!isRow(o) || 'unit_type' in o) return o
  const key = String(o.key ?? '')
  const en = typeof o.label === 'string' ? o.label : key
  const kind = o.kind
  return {
    ...o,
    key,
    hi: OUTPUT_HI[key] ?? en,
    en,
    unit_type: o.unitType,
    // A product follows the run's stream; by-products and waste are always ordinary.
    stream: kind === 'PRODUCT' ? 'RUN' : 'CONV',
    product: kind === 'PRODUCT',
    to_packing: o.toPacking === true,
    grade: o.grade ?? null,
  }
}

/** One registry step (`code`, `label`, `lossLimitPct` …) → the phone's step row. */
export function stepFromServer(s: unknown): unknown {
  if (!isRow(s) || 'step_code' in s) return s
  const code = String(s.code ?? '')
  const en = typeof s.label === 'string' ? s.label : code
  const points = Array.isArray(s.cleanoutPhotoPoints) ? s.cleanoutPhotoPoints : []
  return {
    ...s,
    step_code: code,
    hi: STEP_HI[code] ?? en,
    en,
    link_type: s.linkType,
    loss_limit_pct: num(s.lossLimitPct),
    outputs: Array.isArray(s.outputs) ? s.outputs.map(output) : [],
    photo_points: points.map((p) =>
      typeof p === 'string' ? { key: p, ...(PHOTO[p] ?? { hi: p, en: p }) } : p,
    ),
    settings_fields: Array.isArray(s.settingsFields) ? s.settingsFields : [],
    needs_moisture: MOISTURE_STEPS.has(code),
  }
}

/** One settings row: percents as numbers, `_kg` sizes as grams. */
export function yieldBandFromServer(b: unknown): unknown {
  if (!isRow(b)) return b
  return {
    ...b,
    min_pct: num(b.min_pct),
    max_pct: num(b.max_pct),
    min_flush_g: b.min_flush_g ?? kgToG(b.min_flush_kg),
    min_input_g: b.min_input_g ?? kgToG(b.min_input_kg),
  }
}

const PRODUCED = new Set(['BATCH', 'BYPRODUCT', 'REJECT'])

/** One bag row (`tag`, `place_id`, kilos as strings, its lot's fields as `unit_*`) → SiteBag. */
export function bagFromServer(b: unknown): unknown {
  if (!isRow(b) || 'qr_payload' in b) return b
  return {
    ...b,
    qr_payload: b.tag,
    // Bags of a run's outputs carry OUTPUT_BAG tags; farmer lots carry gate BAG tags.
    tag_kind: PRODUCED.has(String(b.unit_type)) ? 'OUTPUT_BAG' : 'BAG',
    location_id: b.location_id ?? b.place_id,
    net_g_gate: b.net_g_gate ?? kgToG(b.net_kg_gate),
    net_g_current: b.net_g_current ?? kgToG(b.net_kg_current),
    tare_g: b.tare_g ?? kgToG(b.tare_kg) ?? undefined,
  }
}

/** The server sends no `lots` list; each bag row names its lot, so collect them from there. */
export function lotsFromServerBags(bags: unknown[]): Row[] {
  const lots = new Map<string, Row>()
  for (const b of bags) {
    if (!isRow(b) || typeof b.unit_id !== 'string' || lots.has(b.unit_id)) continue
    lots.set(b.unit_id, {
      id: b.unit_id,
      code: b.unit_code,
      unit_type: b.unit_type ?? 'LOT',
      crop_id: b.crop_id,
      crop_name: b.crop_name ?? null,
      crop_variety_id: b.crop_variety_id ?? null,
      farmer_id: b.farmer_id ?? null,
      farmer_code: b.farmer_code ?? null,
      season_code: b.season_code ?? null,
      stream: b.unit_stream ?? b.stream,
      hold: b.unit_hold ?? 'NONE',
      status: b.unit_status ?? 'STORED',
    })
  }
  return [...lots.values()]
}
